// Catalog Shell — dashboard-context detection
// ============================================================================
//
// THE helper for "am I running inside the dashboard shell, and on which port?"
// Promoted from `apps/cid/src/client/state/dashboard-context.ts` (docs/relay.md
// §6), which held the only implementation that survives external redirects.
// Three divergent ad-hoc detectors existed before this; new code uses this one.
//
// How detection works. The dashboard appends `?dp=<dashboardPort>` to every
// module iframe URL (`apps/desktop/src/client/dashboard.ts`), so its presence
// on load means "hosted by the dashboard". The value is captured ONCE at module
// load, so later History API mutations cannot flip the answer mid-session.
//
// Why sessionStorage. Some flows leave the origin entirely — Stripe Checkout,
// OIDC RPs — and return to a success URL with no `?dp=`. A naive query-string
// check would then decide "standalone" and start aiming calls at the wrong
// place. So the first load that DOES see `?dp=` writes it to sessionStorage and
// later loads in the same browsing context read it back. sessionStorage is
// per-tab/per-iframe, so a genuinely standalone tab never sees a value.
//
// `detectDashboardContext` takes its environment as a parameter purely so it
// can be exercised without a DOM; callers use the captured singletons below.
// ============================================================================

export const DASHBOARD_PORT_STORAGE_KEY = 'catalog_dashboard_dp_v1';

export interface DashboardContext {
    /** True when this document is hosted inside the dashboard shell. */
    isDashboard: boolean;
    /** The dashboard's HTTP/WS port, when known. */
    dashboardPort?: number;
}

/** The pieces of the browser this helper reads. Injectable for tests. */
export interface DashboardContextEnv {
    /** `window.location.search`. */
    search?: string;
    /** `sessionStorage`, or null when unavailable. */
    storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

function browserEnv(): DashboardContextEnv {
    try {
        return {
            search: typeof window !== 'undefined' ? window.location.search : '',
            storage: typeof sessionStorage !== 'undefined' ? sessionStorage : null,
        };
    } catch {
        // SSR, a sandboxed iframe, or a test runner without a DOM.
        return { search: '', storage: null };
    }
}

/**
 * Resolve the dashboard context from a query string plus persisted state.
 * Writing to storage is best-effort: a blocked write must not change the
 * answer for this load.
 */
export function detectDashboardContext(env: DashboardContextEnv = browserEnv()): DashboardContext {
    const { search = '', storage } = env;

    let fromUrl: string | null = null;
    try {
        fromUrl = new URLSearchParams(search).get('dp');
    } catch {
        fromUrl = null;
    }

    if (fromUrl && /^\d+$/.test(fromUrl)) {
        try { storage?.setItem(DASHBOARD_PORT_STORAGE_KEY, fromUrl); } catch { /* not persistable */ }
        return { isDashboard: true, dashboardPort: Number(fromUrl) };
    }

    let fromStorage: string | null = null;
    try { fromStorage = storage?.getItem(DASHBOARD_PORT_STORAGE_KEY) ?? null; } catch { fromStorage = null; }

    if (fromStorage && /^\d+$/.test(fromStorage)) {
        return { isDashboard: true, dashboardPort: Number(fromStorage) };
    }

    return { isDashboard: false };
}

/** The dashboard hub WebSocket URL for a context, or null when standalone. */
export function dashboardWsUrl(context: DashboardContext): string | null {
    return context.dashboardPort ? `ws://localhost:${context.dashboardPort}/ws` : null;
}

/** The dashboard's HTTP origin for a context, or null when standalone. */
export function dashboardHttpBase(context: DashboardContext): string | null {
    return context.dashboardPort ? `http://localhost:${context.dashboardPort}` : null;
}

// ── Captured singleton (the normal entry points) ────────────────────────────

const captured = detectDashboardContext();

/** True when this bundle is running as a dashboard module. */
export function isDashboardContext(): boolean {
    return captured.isDashboard;
}

/** The dashboard's port as captured at load time, or undefined when standalone. */
export function getDashboardPort(): number | undefined {
    return captured.dashboardPort;
}

/** The captured context object. */
export function getDashboardContext(): DashboardContext {
    return captured;
}

/** Hub WebSocket URL for this document, or null when standalone. */
export function getDashboardWsUrl(): string | null {
    return dashboardWsUrl(captured);
}
